// src/pages/UserProfile.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useWeb3 } from '../contexts/Web3Context';
import { useNotification } from '../contexts/NotificationContext';
import axios from 'axios';
import {
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  LinkIcon,
  DocumentTextIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';

function UserProfile() {
  const { address } = useParams();
  const { account, signMessage } = useWeb3();
  const { showNotification } = useNotification();

  const [userInfo, setUserInfo] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userVotes, setUserVotes] = useState([]);
  const [userProposals, setUserProposals] = useState([]);
  const [userTokens, setUserTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 編集モード状態
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedBio, setEditedBio] = useState('');
  const [editedWebsite, setEditedWebsite] = useState('');
  const [editedTwitter, setEditedTwitter] = useState('');
  const [editedTelegram, setEditedTelegram] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const isOwnProfile = address === account;

  // ユーザー情報・投票・提案の取得
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError('');

        // ユーザー情報取得
        const userInfoResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/${address}`
        );
        setUserInfo(userInfoResponse.data);

        // プロフィール情報取得
        const profileResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/profile/${address}`
        );
        setUserProfile(profileResponse.data);
        setEditedName(profileResponse.data.displayName || '');
        setEditedBio(profileResponse.data.bio || '');
        setEditedWebsite(profileResponse.data.website || '');
        setEditedTwitter(profileResponse.data.twitter || '');
        setEditedTelegram(profileResponse.data.telegram || '');

        // 投票履歴取得
        const votesResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/votes/user/${address}`
        );
        setUserVotes(votesResponse.data);

        // 提案取得
        const proposalsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/proposals/user/${address}`
        );
        setUserProposals(proposalsResponse.data);

        // 発行トークン取得
        const tokensResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/${address}/tokens`
        );
        setUserTokens(tokensResponse.data);

        setLoading(false);
      } catch (err) {
        console.error('User data fetch error:', err);
        setError('ユーザー情報の取得に失敗しました');
        setLoading(false);
      }
    };

    fetchUserData();
  }, [address]);

  // プロフィール更新
  const handleSaveProfile = async () => {
    if (!account) {
      showNotification('ウォレットを接続してください', 'error');
      return;
    }

    try {
      setIsSaving(true);

      // 署名メッセージの作成
      const message = `プロフィールを更新します\nアドレス: ${address}\n${Date.now()}`;

      // 署名
      const signature = await signMessage(message);

      // プロフィールデータの準備
      const profileData = {
        address,
        displayName: editedName.trim() || null,
        bio: editedBio.trim() || null,
        website: editedWebsite.trim() || null,
        twitter: editedTwitter.trim() || null,
        telegram: editedTelegram.trim() || null,
        signature,
        message,
      };

      // APIリクエスト
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/users/profile`,
        profileData
      );

      setUserProfile(response.data);
      setIsEditing(false);
      showNotification('プロフィールを更新しました', 'success');
    } catch (error) {
      console.error('プロフィール更新エラー:', error);
      showNotification(error.response?.data?.error || 'プロフィールの更新に失敗しました', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // 編集モードキャンセル
  const handleCancelEdit = () => {
    setEditedName(userProfile?.displayName || '');
    setEditedBio(userProfile?.bio || '');
    setEditedWebsite(userProfile?.website || '');
    setEditedTwitter(userProfile?.twitter || '');
    setEditedTelegram(userProfile?.telegram || '');
    setIsEditing(false);
  };

  // アドレスを短縮
  const shortenAddress = addr => {
    if (!addr) return '';
    return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 p-4 rounded-md inline-block">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden mb-8">
        <div className="p-6">
          <div className="flex justify-between items-start">
            <div className="flex items-start">
              <div className="flex-shrink-0 mr-4">
                <div className="h-20 w-20 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-300 text-2xl font-bold">
                  {userProfile?.displayName
                    ? userProfile.displayName.charAt(0).toUpperCase()
                    : address.charAt(2).toUpperCase()}
                </div>
              </div>

              <div>
                {isEditing ? (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      表示名
                    </label>
                    <input
                      type="text"
                      value={editedName}
                      onChange={e => setEditedName(e.target.value)}
                      placeholder="表示名"
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      maxLength={50}
                    />
                  </div>
                ) : (
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-1">
                    {userProfile?.displayName || `${shortenAddress(address)} のプロフィール`}
                  </h1>
                )}

                <div className="text-gray-500 dark:text-gray-400 font-mono text-sm mb-2">
                  {address}
                </div>

                {userInfo && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {userInfo.status?.isAdmin && (
                      <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200 rounded-full text-xs">
                        管理者
                      </span>
                    )}
                    {userInfo.status?.isBanned && (
                      <span className="px-2 py-0.5 bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 rounded-full text-xs">
                        BAN済み
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {isOwnProfile && (
              <div>
                {isEditing ? (
                  <div className="flex gap-2">
                    <button
                      onClick={handleCancelEdit}
                      className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      disabled={isSaving}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={handleSaveProfile}
                      className="p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                      disabled={isSaving}
                    >
                      {isSaving ? <LoadingSpinner size="sm" /> : <CheckIcon className="h-5 w-5" />}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            )}
          </div>

          {isEditing ? (
            <div className="mt-4 space-y-4">
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  自己紹介
                </label>
                <textarea
                  value={editedBio}
                  onChange={e => setEditedBio(e.target.value)}
                  placeholder="自己紹介"
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  rows="3"
                  maxLength={500}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  ウェブサイト
                </label>
                <input
                  type="text"
                  value={editedWebsite}
                  onChange={e => setEditedWebsite(e.target.value)}
                  placeholder="https://example.com"
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Twitter
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                      @
                    </span>
                    <input
                      type="text"
                      value={editedTwitter}
                      onChange={e => setEditedTwitter(e.target.value)}
                      placeholder="username"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-r-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Telegram
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                      @
                    </span>
                    <input
                      type="text"
                      value={editedTelegram}
                      onChange={e => setEditedTelegram(e.target.value)}
                      placeholder="username"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-r-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                </div>
              </div> */}
            </div>
          ) : (
            <div className="mt-4">
              {userProfile?.bio && (
                <div className="mb-4 text-gray-700 dark:text-gray-300">{userProfile.bio}</div>
              )}

              <div className="flex flex-wrap gap-3">
                {userProfile?.website && (
                  <a
                    href={
                      userProfile.website.startsWith('http')
                        ? userProfile.website
                        : `https://${userProfile.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <LinkIcon className="h-4 w-4 mr-1" />
                    {userProfile.website}
                  </a>
                )}

                {userProfile?.twitter && (
                  <a
                    href={`https://twitter.com/${userProfile.twitter}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                    </svg>
                    @{userProfile.twitter}
                  </a>
                )}

                {userProfile?.telegram && (
                  <a
                    href={`https://t.me/${userProfile.telegram}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.57-1.39-.93-2.23-1.47-.99-.65-.35-1 .22-1.59.15-.15 2.63-2.42 2.68-2.63.01-.03.01-.14-.06-.2-.07-.06-.19-.04-.27-.02-.12.02-1.91 1.21-5.42 3.58-.51.35-.97.52-1.4.51-.46-.01-1.34-.26-2-.47-.8-.26-1.45-.4-1.4-.85.03-.22.45-.45 1.28-.68 5.02-2.18 8.37-3.62 10.03-4.32 1.78-.74 2.15-.88 2.4-.88.05 0 .18.01.26.08.13.11.13.32.14.45 0 .06.01.19-.02.32z" />
                    </svg>
                    @{userProfile.telegram}
                  </a>
                )}
              </div>
            </div>
          )}

          {userInfo && (
            <div className="mt-6 grid grid-cols-3 gap-4 border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                  {userInfo.stats?.issuedTokens || 0}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">発行コイン</div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                  {userInfo.stats?.createdProposals || 0}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">提案数</div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                  {userInfo.stats?.votesCount || 0}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">投票数</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* 作成した提案 */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
          <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex items-center">
            <DocumentTextIcon className="h-5 w-5 text-gray-500 dark:text-gray-300 mr-2" />
            <h2 className="font-bold text-gray-800 dark:text-gray-200">提案履歴</h2>
          </div>

          {userProposals.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              作成した提案はありません。
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {userProposals.map(proposal => {
                const now = new Date();
                const startDate = new Date(proposal.startTime);
                const endDate = new Date(proposal.endTime);
                const status = now < startDate ? '準備中' : now > endDate ? '終了' : '進行中';

                return (
                  <Link
                    key={proposal.id}
                    to={`/proposals/${proposal.id}`}
                    className="block hover:bg-gray-50 dark:hover:bg-gray-700 p-4"
                  >
                    <div className="flex justify-between items-start">
                      <h3 className="font-medium text-gray-800 dark:text-gray-200">
                        {proposal.title}
                      </h3>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          status === '準備中'
                            ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                            : status === '進行中'
                            ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                            : 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200'
                        }`}
                      >
                        {status}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {new Date(proposal.startTime).toLocaleDateString()} ~{' '}
                      {new Date(proposal.endTime).toLocaleDateString()}
                    </div>
                    <div className="text-sm mt-1">
                      <span className="text-blue-600 dark:text-blue-400">
                        {proposal.token?.symbol || '?'}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400 ml-2">
                        総投票数: {proposal.votes?.length || 0}票
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* 投票履歴 */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
          <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex items-center">
            <DocumentTextIcon className="h-5 w-5 text-gray-500 dark:text-gray-300 mr-2" />
            <h2 className="font-bold text-gray-800 dark:text-gray-200">投票履歴</h2>
          </div>

          {userVotes.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              投票履歴はありません。
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {userVotes.map(vote => (
                <Link
                  key={vote.id}
                  to={`/proposals/${vote.proposalId}`}
                  className="block hover:bg-gray-50 dark:hover:bg-gray-700 p-4"
                >
                  <div className="font-medium text-gray-800 dark:text-gray-200">
                    {vote.proposal?.title || '不明な提案'}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    選択肢: {vote.choice?.text || '不明な選択肢'}
                    <span className="mx-2">•</span>
                    投票力: {vote.weight} 票
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    投票日時: {new Date(vote.createdAt).toLocaleDateString()}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 発行トークン */}
      {userTokens.length > 0 && (
        <div className="mt-8 bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
          <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex items-center">
            <CubeIcon className="h-5 w-5 text-gray-500 dark:text-gray-300 mr-2" />
            <h2 className="font-bold text-gray-800 dark:text-gray-200">発行コイン</h2>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {userTokens.map(token => (
              <Link
                key={token.id}
                to={`/tokens/${token.launchToken || token.grantToken}`}
                className="block hover:bg-gray-50 dark:hover:bg-gray-700 p-4"
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0 mr-3">
                    {/* トークン画像を表示 */}
                    {token.img ? (
                      <img
                        src={token.img}
                        alt={token.name}
                        className="h-10 w-10 rounded-full object-cover"
                        onError={e => {
                          // 画像読み込みエラー時にフォールバック表示
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}

                    {/* フォールバックアイコン - 画像がないか読み込みエラー時に表示 */}
                    <div
                      className={`h-10 w-10 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-300 font-bold ${
                        token.img ? 'hidden' : 'flex'
                      }`}
                    >
                      {token.symbol ? token.symbol.substring(0, 2).toUpperCase() : 'TK'}
                    </div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-800 dark:text-gray-200">
                      {token.name}
                      <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                        ({token.symbol})
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      上場日:{' '}
                      {token.launchedAt
                        ? new Date(token.launchedAt).toLocaleDateString()
                        : new Date(token.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default UserProfile;
